import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { usersApi, metaApi, googleAdsApi, reportsApi, clinicorpApi } from '../services/api';
import toast from 'react-hot-toast';
import CrmIntegrationTab from '../components/settings/CrmIntegrationTab';

const Settings = () => {
  const { user } = useAuth();
  
  const AVAILABLE_REPORTS = [
    { id: '/reports', label: 'Relatórios Meta' },
    { id: '/page-reports', label: 'Relatórios Páginas' },
    { id: '/franchise-reports', label: 'Relatório Franquia' },
    { id: '/google-ads-reports', label: 'Relatórios Google Ads' },
    { id: '/combined-reports', label: 'Relatórios Combinados' },
    { id: '/clinicorp-reports', label: 'Relatórios Clinicorp' },
    { id: '/franchise-v2', label: 'Relatório Franquia V2' },
    { id: '/conversions', label: 'Rastreamento de Conversões (WhatsApp CTWA)' },
    { id: '/clinic-data', label: 'Dados das Clínicas' },
    { id: '/forms', label: 'Formulários' }
  ];
  
  const [activeTab, setActiveTab] = useState('users');

  // Admin User Management State
  const [users, setUsers] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Shared Links State
  const [shares, setShares] = useState([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  // Clinicorp State
  const [clinicorpAccounts, setClinicorpAccounts] = useState([]);
  const [allClinicorpAccounts, setAllClinicorpAccounts] = useState([]);
  const [isLoadingClinicorp, setIsLoadingClinicorp] = useState(false);
  const [isClinicorpModalOpen, setIsClinicorpModalOpen] = useState(false);
  const [editingClinicorp, setEditingClinicorp] = useState(null);
  const [clinicorpForm, setClinicorpForm] = useState({ name: '', username: '', password: '', subscriberId: '' });
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    roleType: 'USER',
    adAccountIds: [],
    pageIds: [],
    accessibleReports: [],
    googleAdsAccountIds: [],
    clinicorpAccountIds: [],
    aiAssistantEnabled: true
  });

  useEffect(() => {
    if (user?.roleType === 'ADMIN') {
      fetchUsersAndAccounts();
    }
  }, [user]);

  useEffect(() => {
    if (user && activeTab === 'links') {
      fetchShares();
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user && activeTab === 'clinicorp') {
      fetchClinicorpAccounts();
    }
  }, [user, activeTab]);

  const fetchShares = async () => {
    setIsLoadingShares(true);
    try {
      const res = await reportsApi.listShares();
      setShares(res.data.shares || []);
    } catch (error) {
      toast.error('Erro ao buscar links compartilhados');
    } finally {
      setIsLoadingShares(false);
    }
  };

  const handleRevokeShare = async (id) => {
    if (!window.confirm('Revogar este link? Quem tiver o link não conseguirá mais acessar.')) return;
    try {
      await reportsApi.deleteShare(id);
      toast.success('Link revogado');
      setShares(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      toast.error(error.message || 'Erro ao revogar link');
    }
  };

  const copyShareLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/shared/${token}`);
    toast.success('Link copiado!');
  };

  const formatExpiry = (share) => {
    if (!share.expiresAt) return <span className="px-2 py-0.5 rounded-md bg-green-100 text-green-700 text-[10px] font-bold">Permanente</span>;
    const expired = new Date() > new Date(share.expiresAt);
    if (expired) return <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-600 text-[10px] font-bold">Expirado</span>;
    return <span className="px-2 py-0.5 rounded-md bg-yellow-100 text-yellow-700 text-[10px] font-bold">{new Date(share.expiresAt).toLocaleDateString('pt-BR')}</span>;
  };

  const fetchClinicorpAccounts = async () => {
    setIsLoadingClinicorp(true);
    try {
      const res = await clinicorpApi.getAccounts();
      setClinicorpAccounts(res.data.accounts || []);
    } catch (error) {
      toast.error('Erro ao buscar contas Clinicorp');
    } finally {
      setIsLoadingClinicorp(false);
    }
  };

  const fetchAllClinicorpAccounts = async () => {
    try {
      const res = await clinicorpApi.getAllAccounts();
      setAllClinicorpAccounts(res.data.accounts || []);
    } catch (e) {}
  };

  const openAddClinicorp = () => {
    setEditingClinicorp(null);
    setClinicorpForm({ name: '', username: '', password: '', subscriberId: '' });
    setIsClinicorpModalOpen(true);
  };

  const openEditClinicorp = (account) => {
    setEditingClinicorp(account);
    setClinicorpForm({ name: account.name, username: account.username, password: '', subscriberId: account.subscriberId });
    setIsClinicorpModalOpen(true);
  };

  const handleClinicorpSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClinicorp) {
        const payload = { ...clinicorpForm };
        if (!payload.password) delete payload.password;
        await clinicorpApi.updateAccount(editingClinicorp.id, payload);
        toast.success('Conta atualizada');
      } else {
        if (!clinicorpForm.password) { toast.error('Senha obrigatória'); return; }
        await clinicorpApi.createAccount(clinicorpForm);
        toast.success('Conta criada');
      }
      setIsClinicorpModalOpen(false);
      fetchClinicorpAccounts();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar conta Clinicorp');
    }
  };

  const handleDeleteClinicorp = async (id) => {
    if (!window.confirm('Remover esta conta Clinicorp? Acesso de todos os usuários será revogado.')) return;
    try {
      await clinicorpApi.deleteAccount(id);
      toast.success('Conta removida');
      setClinicorpAccounts(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      toast.error(error.message || 'Erro ao remover conta');
    }
  };

  const reportTypeLabel = (type) => {
    const map = { ad_account: 'Meta Ads', page: 'Página', franchise: 'Franquia', combined: 'Combinado' };
    return map[type] || type;
  };

  const fetchUsersAndAccounts = async () => {
    setIsLoading(true);
    try {
      const usersRes = await usersApi.getUsers();
      const accountsRes = await metaApi.getAllAccountsFromDb();
      let pagesRes = { data: { pages: [] } };
      let googleAdsRes = { data: { accounts: [] } };
      try { pagesRes = await metaApi.getPages(); } catch (e) { console.error("Failed to fetch pages", e); }
      try { googleAdsRes = await googleAdsApi.getAllAccountsFromDb(); } catch (e) { console.error("Failed to fetch Google Ads accounts", e); }
      setUsers(usersRes.data.users || []);
      setAdAccounts(accountsRes.data.accounts || []);
      setPages(pagesRes.data?.pages || []);
      setGoogleAdsAccounts(googleAdsRes.data?.accounts || []);
      await fetchAllClinicorpAccounts();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao buscar dados de usuários e contas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClinicorpToggle = (accountId) => {
    setFormData(prev => {
      const isSelected = prev.clinicorpAccountIds.includes(accountId);
      return {
        ...prev,
        clinicorpAccountIds: isSelected
          ? prev.clinicorpAccountIds.filter(id => id !== accountId)
          : [...prev.clinicorpAccountIds, accountId]
      };
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      roleType: 'USER',
      adAccountIds: [],
      pageIds: [],
      accessibleReports: [],
      googleAdsAccountIds: [],
      clinicorpAccountIds: [],
      aiAssistantEnabled: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email,
      password: '',
      roleType: userToEdit.roleType,
      adAccountIds: userToEdit.userAccounts ? userToEdit.userAccounts.map(a => a.account.id) : [],
      pageIds: userToEdit.userPages ? userToEdit.userPages.map(p => p.pageId) : [],
      accessibleReports: userToEdit.accessibleReports || [],
      googleAdsAccountIds: userToEdit.userGoogleAdsAccounts ? userToEdit.userGoogleAdsAccounts.map(a => a.account.id) : [],
      clinicorpAccountIds: userToEdit.userClinicorpAccounts ? userToEdit.userClinicorpAccounts.map(a => a.account.id) : [],
      aiAssistantEnabled: userToEdit.aiAssistantEnabled !== false
    });
    setIsModalOpen(true);
  };

  const handleAccountToggle = (accountId) => {
    setFormData(prev => {
      const isSelected = prev.adAccountIds.includes(accountId);
      let newSelection;
      if (isSelected) {
        newSelection = prev.adAccountIds.filter(id => id !== accountId);
      } else {
        newSelection = [...prev.adAccountIds, accountId];
      }
      return { ...prev, adAccountIds: newSelection };
    });
  };

  const handlePageToggle = (pageId) => {
    setFormData(prev => {
      const isSelected = prev.pageIds.includes(pageId);
      let newSelection;
      if (isSelected) {
        newSelection = prev.pageIds.filter(id => id !== pageId);
      } else {
        newSelection = [...prev.pageIds, pageId];
      }
      return { ...prev, pageIds: newSelection };
    });
  };

  const handleGoogleAdsToggle = (accountId) => {
    setFormData(prev => {
      const isSelected = prev.googleAdsAccountIds.includes(accountId);
      return {
        ...prev,
        googleAdsAccountIds: isSelected
          ? prev.googleAdsAccountIds.filter(id => id !== accountId)
          : [...prev.googleAdsAccountIds, accountId]
      };
    });
  };

  const handleReportToggle = (reportId) => {
    setFormData(prev => {
      const isSelected = prev.accessibleReports.includes(reportId);
      let newSelection;
      if (isSelected) {
        newSelection = prev.accessibleReports.filter(id => id !== reportId);
      } else {
        newSelection = [...prev.accessibleReports, reportId];
      }
      return { ...prev, accessibleReports: newSelection };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update
        const updatePayload = { ...formData };
        if (!updatePayload.password) delete updatePayload.password;
        await usersApi.updateUser(editingUser.id, updatePayload);
        toast.success('Usuário atualizado com sucesso');
      } else {
        // Create
        if (!formData.password) {
          toast.error('Senha é obrigatória para novos usuários');
          return;
        }
        await usersApi.createUser(formData);
        toast.success('Usuário criado com sucesso');
      }
      setIsModalOpen(false);
      fetchUsersAndAccounts();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja desativar este usuário?')) {
      try {
        await usersApi.deleteUser(id);
        toast.success('Usuário desativado com sucesso');
        fetchUsersAndAccounts();
      } catch (error) {
        toast.error(error.message || 'Erro ao deletar usuário');
      }
    }
  };

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-12">
        <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Configurações</h1>
        <p className="text-secondary font-body">Configure sua plataforma Meta Ads</p>
      </div>

      <div className="grid grid-cols-1 gap-8 mb-12 max-w-xl">
        {/* Preferências da Conta (Existing) */}
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
          <h2 className="font-headline font-bold text-2xl text-on-surface mb-6 flex items-center gap-2">
             <span className="material-symbols-outlined text-primary">manage_accounts</span> Preferências da Conta
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold font-headline text-on-surface mb-2">
                Notificações por Email
              </label>
              <select className="input w-full">
                <option>Ativado</option>
                <option>Desativado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold font-headline text-on-surface mb-2">
                Programação de Relatórios
              </label>
              <select className="input w-full">
                <option>Diário</option>
                <option>Semanal</option>
                <option>Mensal</option>
                <option>Nunca</option>
              </select>
            </div>
            <button className="border border-outline-variant hover:bg-surface-container transition-colors text-on-surface w-full rounded-full py-3 font-bold font-headline mt-4">
              Atualizar Preferências
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-outline-variant/30">
        {user?.roleType === 'ADMIN' && (
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 text-sm font-bold font-headline border-b-2 transition-colors ${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-on-surface'}`}
          >
            <span className="material-symbols-outlined text-[16px] align-middle mr-1">admin_panel_settings</span>
            Usuários
          </button>
        )}
        <button
          onClick={() => setActiveTab('links')}
          className={`px-4 py-2 text-sm font-bold font-headline border-b-2 transition-colors ${activeTab === 'links' ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-on-surface'}`}
        >
          <span className="material-symbols-outlined text-[16px] align-middle mr-1">link</span>
          Links Compartilhados
        </button>
        <button
          onClick={() => setActiveTab('clinicorp')}
          className={`px-4 py-2 text-sm font-bold font-headline border-b-2 transition-colors ${activeTab === 'clinicorp' ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-on-surface'}`}
        >
          <span className="material-symbols-outlined text-[16px] align-middle mr-1">local_hospital</span>
          Clinicorp
        </button>
        {user?.roleType === 'ADMIN' && (
          <button
            onClick={() => setActiveTab('crm')}
            className={`px-4 py-2 text-sm font-bold font-headline border-b-2 transition-colors ${activeTab === 'crm' ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-on-surface'}`}
          >
            <span className="material-symbols-outlined text-[16px] align-middle mr-1">hub</span>
            Integração CRM
          </button>
        )}
      </div>

      {/* CRM Integration Tab (admin, read-only) */}
      {activeTab === 'crm' && user?.roleType === 'ADMIN' && <CrmIntegrationTab />}

      {/* Users Tab */}
      {activeTab === 'users' && user?.roleType === 'ADMIN' && (
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)] mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">admin_panel_settings</span> Gerenciamento de Usuários
            </h2>
            <button onClick={openAddModal} className="primary-pill-gradient text-white px-4 py-2 rounded-full font-bold font-headline text-sm hover:opacity-90 transition-all flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">person_add</span> Criar Usuário
            </button>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <p className="py-4 text-center text-secondary">Carregando usuários...</p>
            ) : (
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-outline-variant/30 text-left text-sm text-secondary">
                    <th className="pb-3">Usuário</th>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Função</th>
                    <th className="pb-3">Contas de Acesso</th>
                    <th className="pb-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-outline-variant/10 text-sm">
                      <td className="py-4 font-bold text-on-surface">{u.username} {!u.isActive && <span className="text-xs text-red-500 font-normal ml-2">(Desativado)</span>}</td>
                      <td className="py-4 text-secondary">{u.email}</td>
                      <td className="py-4 text-secondary">
                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${u.roleType === 'ADMIN' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'}`}>
                          {u.roleType}
                        </span>
                      </td>
                      <td className="py-4 text-secondary">
                        {u.roleType === 'ADMIN' ? (
                          <span className="text-xs italic">Todas as contas e páginas</span>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap gap-1">
                              {u.userAccounts?.length > 0 ? (
                                u.userAccounts.map(a => (
                                  <span key={a.account.id} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-[10px] truncate max-w-[120px]" title={`Meta: ${a.account.name}`}>
                                    {a.account.name}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[10px] text-slate-400">Nenhuma conta Meta</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {u.userGoogleAdsAccounts?.length > 0 ? (
                                u.userGoogleAdsAccounts.map(a => (
                                  <span key={a.account.id} className="bg-green-50 text-green-700 px-2 py-1 rounded-md text-[10px] truncate max-w-[120px]" title={`Google: ${a.account.name}`}>
                                    {a.account.name}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[10px] text-slate-400">Nenhuma conta Google</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {u.userPages?.length > 0 ? (
                                u.userPages.map(p => (
                                  <span key={p.pageId} className="bg-primary/10 text-primary px-2 py-1 rounded-md text-[10px] truncate max-w-[120px]" title={`Página: ${p.pageName || p.pageId}`}>
                                    {p.pageName || p.pageId}
                                  </span>
                                ))
                              ) : null}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="py-4 text-right">
                        <button onClick={() => openEditModal(u)} className="text-slate-400 hover:text-primary transition-colors p-1" title="Editar">
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        {user.id !== u.id && u.isActive && (
                          <button onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1 ml-2" title="Desativar">
                            <span className="material-symbols-outlined text-[20px]">person_off</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-4 text-center text-secondary">Nenhum usuário encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Links Compartilhados Tab */}
      {activeTab === 'links' && (
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)] mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">link</span> Links Compartilhados
            </h2>
            <button onClick={fetchShares} className="border border-outline-variant hover:bg-surface-container transition-colors text-on-surface px-3 py-1.5 rounded-full text-sm font-bold font-headline flex items-center gap-1">
              <span className="material-symbols-outlined text-[16px]">refresh</span> Atualizar
            </button>
          </div>

          <div className="overflow-x-auto">
            {isLoadingShares ? (
              <p className="py-4 text-center text-secondary">Carregando links...</p>
            ) : shares.length === 0 ? (
              <p className="py-8 text-center text-secondary">Nenhum link compartilhado encontrado.</p>
            ) : (
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-outline-variant/30 text-left text-sm text-secondary">
                    <th className="pb-3">Nome / Conta</th>
                    <th className="pb-3">Tipo</th>
                    <th className="pb-3">Criado por</th>
                    <th className="pb-3">Criado em</th>
                    <th className="pb-3">Validade</th>
                    <th className="pb-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {shares.map(share => (
                    <tr key={share.id} className="border-b border-outline-variant/10 text-sm">
                      <td className="py-4">
                        <p className="font-bold text-on-surface">{share.label || share.entityName || '—'}</p>
                        {share.adAccount && (
                          <p className="text-[10px] text-secondary mt-0.5">{share.adAccount.name} ({share.adAccount.currency})</p>
                        )}
                      </td>
                      <td className="py-4 text-secondary">
                        <span className="px-2 py-0.5 rounded-md bg-surface-container text-xs font-bold">{reportTypeLabel(share.reportType)}</span>
                      </td>
                      <td className="py-4 text-secondary text-xs">{share.creator?.username || '—'}</td>
                      <td className="py-4 text-secondary text-xs">{new Date(share.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td className="py-4">{formatExpiry(share)}</td>
                      <td className="py-4 text-right">
                        <button
                          onClick={() => copyShareLink(share.token)}
                          className="text-slate-400 hover:text-primary transition-colors p-1"
                          title="Copiar link"
                        >
                          <span className="material-symbols-outlined text-[20px]">content_copy</span>
                        </button>
                        <button
                          onClick={() => handleRevokeShare(share.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors p-1 ml-2"
                          title="Revogar link"
                        >
                          <span className="material-symbols-outlined text-[20px]">link_off</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Clinicorp Tab */}
      {activeTab === 'clinicorp' && (
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)] mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">local_hospital</span> Contas Clinicorp
            </h2>
            {user?.roleType === 'ADMIN' && (
              <button onClick={openAddClinicorp} className="primary-pill-gradient text-white px-4 py-2 rounded-full font-bold font-headline text-sm hover:opacity-90 transition-all flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">add</span> Adicionar Conta
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            {isLoadingClinicorp ? (
              <p className="py-4 text-center text-secondary">Carregando contas...</p>
            ) : clinicorpAccounts.length === 0 ? (
              <p className="py-8 text-center text-secondary">Nenhuma conta Clinicorp cadastrada.</p>
            ) : (
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-outline-variant/30 text-left text-sm text-secondary">
                    <th className="pb-3">Nome</th>
                    <th className="pb-3">Usuário</th>
                    <th className="pb-3">Subscriber ID</th>
                    <th className="pb-3">Criado em</th>
                    {user?.roleType === 'ADMIN' && <th className="pb-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {clinicorpAccounts.map(account => (
                    <tr key={account.id} className="border-b border-outline-variant/10 text-sm">
                      <td className="py-4 font-bold text-on-surface">{account.name}</td>
                      <td className="py-4 text-secondary">{account.username}</td>
                      <td className="py-4">
                        <span className="px-2 py-0.5 rounded-md bg-surface-container text-xs font-mono text-on-surface">{account.subscriberId}</span>
                      </td>
                      <td className="py-4 text-secondary text-xs">{new Date(account.createdAt).toLocaleDateString('pt-BR')}</td>
                      {user?.roleType === 'ADMIN' && (
                        <td className="py-4 text-right">
                          <button onClick={() => openEditClinicorp(account)} className="text-slate-400 hover:text-primary transition-colors p-1" title="Editar">
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                          </button>
                          <button onClick={() => handleDeleteClinicorp(account.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1 ml-2" title="Remover">
                            <span className="material-symbols-outlined text-[20px]">delete</span>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal Clinicorp Create/Edit */}
      {isClinicorpModalOpen && user?.roleType === 'ADMIN' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
            <h3 className="text-xl font-bold font-headline mb-4 text-on-surface">
              {editingClinicorp ? 'Editar Conta Clinicorp' : 'Nova Conta Clinicorp'}
            </h3>
            <form onSubmit={handleClinicorpSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Nome da Conta</label>
                <input
                  type="text"
                  required
                  className="input w-full text-sm"
                  placeholder="Ex: Grupo GPF"
                  value={clinicorpForm.name}
                  onChange={e => setClinicorpForm({ ...clinicorpForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Usuário Clinicorp</label>
                <input
                  type="text"
                  required
                  className="input w-full text-sm"
                  placeholder="Ex: grupogpf"
                  value={clinicorpForm.username}
                  onChange={e => setClinicorpForm({ ...clinicorpForm, username: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">
                  Senha {editingClinicorp && <span className="font-normal text-[10px]">(deixe em branco p/ manter)</span>}
                </label>
                <input
                  type="password"
                  required={!editingClinicorp}
                  className="input w-full text-sm"
                  placeholder="UUID da senha Clinicorp"
                  value={clinicorpForm.password}
                  onChange={e => setClinicorpForm({ ...clinicorpForm, password: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Subscriber ID</label>
                <input
                  type="text"
                  required
                  className="input w-full text-sm"
                  placeholder="Ex: udiclinic2"
                  value={clinicorpForm.subscriberId}
                  onChange={e => setClinicorpForm({ ...clinicorpForm, subscriberId: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsClinicorpModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">
                  Cancelar
                </button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Create/Edit User */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fade-in shadow-2xl">
            <h3 className="text-2xl font-bold font-headline mb-4 text-on-surface">
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Nome de Usuário</label>
                  <input type="text" required className="input w-full text-sm" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Email</label>
                  <input type="email" required className="input w-full text-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Senha {editingUser && <span className="font-normal text-[10px]">(deixe em branco p/ manter)</span>}</label>
                  <input type="password" required={!editingUser} className="input w-full text-sm" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Função</label>
                  <select className="input w-full text-sm" value={formData.roleType} onChange={e => setFormData({...formData, roleType: e.target.value})}>
                    <option value="USER">Usuário Comum</option>
                    <option value="ADMIN">Administrador</option>
                  </select>
                </div>
              </div>

              {formData.roleType === 'USER' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-secondary mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                      Contas Meta Ads
                    </label>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                      {adAccounts.length > 0 ? adAccounts.map((account) => (
                        <label key={account.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-surface-container-low rounded-lg transition-colors">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-primary bg-surface-container rounded border-outline-variant focus:ring-primary focus:ring-2"
                            checked={formData.adAccountIds.includes(account.id)}
                            onChange={() => handleAccountToggle(account.id)}
                          />
                          <span className="text-sm font-medium text-on-surface">{account.name} <span className="text-xs text-secondary">({account.currency})</span></span>
                        </label>
                      )) : (
                        <p className="text-xs text-secondary p-2">Nenhuma conta de anúncio encontrada no banco de dados.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-secondary mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                      Contas Google Ads
                    </label>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                      {googleAdsAccounts.length > 0 ? googleAdsAccounts.map((account) => (
                        <label key={account.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-surface-container-low rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-green-600 bg-surface-container rounded border-outline-variant focus:ring-green-500 focus:ring-2"
                            checked={formData.googleAdsAccountIds.includes(account.id)}
                            onChange={() => handleGoogleAdsToggle(account.id)}
                          />
                          <span className="text-sm font-medium text-on-surface">{account.name} <span className="text-xs text-secondary">({account.currency})</span></span>
                        </label>
                      )) : (
                        <p className="text-xs text-secondary p-2">Nenhuma conta Google Ads no banco de dados.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-secondary mb-2">Páginas Conectadas</label>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                      {pages.length > 0 ? pages.map((page) => (
                        <label key={page.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-surface-container-low rounded-lg transition-colors">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-primary bg-surface-container rounded border-outline-variant focus:ring-primary focus:ring-2"
                            checked={formData.pageIds.includes(page.id)}
                            onChange={() => handlePageToggle(page.id)}
                          />
                          <span className="text-sm font-medium text-on-surface">{page.name}</span>
                        </label>
                      )) : (
                        <p className="text-xs text-secondary p-2">Nenhuma página encontrada.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-secondary mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>
                      Contas Clinicorp
                    </label>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                      {allClinicorpAccounts.length > 0 ? allClinicorpAccounts.map((account) => (
                        <label key={account.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-surface-container-low rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-rose-600 bg-surface-container rounded border-outline-variant focus:ring-rose-500 focus:ring-2"
                            checked={formData.clinicorpAccountIds.includes(account.id)}
                            onChange={() => handleClinicorpToggle(account.id)}
                          />
                          <span className="text-sm font-medium text-on-surface">{account.name} <span className="text-xs text-secondary font-mono">({account.subscriberId})</span></span>
                        </label>
                      )) : (
                        <p className="text-xs text-secondary p-2">Nenhuma conta Clinicorp cadastrada.</p>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-secondary mb-2">Relatórios Permitidos</label>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/30">
                      {AVAILABLE_REPORTS.map((report) => (
                        <label key={report.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-surface-container-low rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-primary bg-surface-container rounded border-outline-variant focus:ring-primary focus:ring-2"
                            checked={formData.accessibleReports.includes(report.id)}
                            onChange={() => handleReportToggle(report.id)}
                          />
                          <span className="text-sm font-medium text-on-surface">{report.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between p-4 bg-surface-container-lowest rounded-xl border border-outline-variant/30">
                      <div>
                        <p className="text-sm font-bold text-on-surface flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-purple-500 inline-block"></span>
                          Assistente de IA
                        </p>
                        <p className="text-xs text-secondary mt-0.5">Permite ao usuário usar o assistente de análise por IA nos relatórios.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, aiAssistantEnabled: !prev.aiAssistantEnabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ml-4 ${formData.aiAssistantEnabled ? 'bg-purple-600' : 'bg-outline-variant'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.aiAssistantEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">
                  Cancelar
                </button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
